import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import "mdb-react-ui-kit/dist/css/mdb.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./App.css";
import ListingAgentPage from "./pages/ListingAgentPage";
// Auth Context
import { AuthProvider, useAuth } from "./context/AuthContext";

// App State Context (for page state persistence)
import { AppStateProvider } from "./context/AppStateContext";

import PosIndex from "./components/POS/pos_index";
import MultiModel from "./components/MultiModel";
// Auth Components
import LoginPage from "./pages/LoginPage";
import LoginAsPage from "./pages/LoginAsPage";
import RegisterPage from "./pages/RegisterPage";

import ChatPage from "./pages/ClaudechatPage";
import DraftProductsPage from "./pages/DraftProductsPage";

// Services
import ServicePages from "./pages/ServicePage";

// DemoPage
import DemoPage from "./pages/DemoPage";
import EbayAutomationPage from "./pages/EbayAutomationPage";
import NotifyVendors from "./pages/Vendornotifypage";
import TestFleekPrice from "./pages/TestFleekPrice";
import SmartScanOverview from "./pages/Smartscanoverview";
// Dashboard Layout
import DashboardLayout from "./components/Layout/DashboardLayout";

// Dashboard Pages
import DashboardPage from "./pages/DashboardPage";
import AddProductPage from "./pages/AddProductPage";
import ViewProductsPage from "./pages/ViewProductsPage";
import VintedProductsPage from "./pages/VintedProductsPage";

import UpdateProductPage from "./pages/UpdateProductPage"; // ← YEH ADD KARO
import ProductDetailsPage from "./pages/ProductDetailsPage";
import ShopifyIntegrationPage from "./pages/ShopifyIntegrationPage";
import EbayIntegrationPage from "./pages/EbayIntegrationPage";
import PoshmarkIntegrationPage from "./pages/PoshmarkIntegrationPage";
import EbaySettingsPage from "./pages/EbaySettingsPage";
import ShopifySettingsPage from "./pages/ShopifySettingsPage";
import GeneralSettingPage from "./pages/GeneralSettingPage";
import InviteUsersPage from "./pages/InviteUsersPage";
import SorterPage from "./pages/SorterPage";
import ChatbotPage from "./pages/ChatbotPage";
import LoggerTester from "./components/LoggerTester";
import KpiDashboard from "./components/KpiDashboard";

//policy privacy page
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import MarketplaceOnboardingPage from "./pages/MarketplaceOnboardingPage";
import EbayIntegratedPage from "./pages/EbayIntegratedPage";
import BatchesPage from "./pages/BatchesPage";
import BatchPrintPage from "./pages/BatchPrintPage";
import OrdersPage from "./pages/OrdersPage";
import OrderDashboardPage from "./pages/OrderDashboardPage";
import {
  loadOnboardingJourney,
  saveOnboardingJourney,
  clearOnboardingJourney,
} from "./utils/onboardingJourney";
import { canAccessPath, getDefaultPathForRole } from "./utils/rolePermissions";

// Google Client ID
const GOOGLE_CLIENT_ID =
  "611740572510-n9ctrclusl3tc1ja253f2ggfg7al7kna.apps.googleusercontent.com";

// =============================================
// PROTECTED ROUTE COMPONENT
// =============================================
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Loading...
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  const featureRaw =
    user?.features ||
    user?.feature ||
    localStorage.getItem("user_feature") ||
    "";
  const featureKey = featureRaw
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  const isSmartScan = featureKey.includes("smartscan");
  const isSmartList = featureKey.includes("smartlist");
  const isAllInOne = featureKey.includes("all_in_one");

  const isStandalonePrintPage = location.pathname.startsWith("/batches/print/");

  if (
    isSmartScan &&
    !isStandalonePrintPage &&
    location.pathname !== "/sorter"
  ) {
    return <Navigate to="/sorter" replace />;
  }

  if (isSmartList && !isAllInOne && location.pathname === "/sorter") {
    return <Navigate to="/dashboard" replace />;
  }

  const userRole = user?.role || "";
  if (!canAccessPath(userRole, location.pathname)) {
    return <Navigate to={getDefaultPathForRole(userRole)} replace />;
  }

  return children;
};

// =============================================
// PUBLIC ROUTE COMPONENT (Allow access even when logged in)
// =============================================
const PublicRoute = ({ children }) => {
  return children;
};

// =============================================
// ROOT REDIRECT (Authenticated -> /dashboard)
// =============================================
const RootRedirect = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Loading...
      </div>
    );
  }

  return isAuthenticated() ? (
    <Navigate to={getDefaultPathForRole(user?.role)} replace />
  ) : (
    <Navigate to="/login" replace />
  );
};

const TrackPage = () => {
  const location = useLocation();

  React.useEffect(() => {
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: location.pathname,
        page_location: window.location.href,
      });
    }
  }, [location]);

  return null;
};

const OnboardingJourneyGuide = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [journey, setJourney] = React.useState(() => loadOnboardingJourney());

  React.useEffect(() => {
    const next = loadOnboardingJourney();
    setJourney(next);
  }, [location.pathname]);

  if (
    !journey?.active ||
    !Array.isArray(journey.steps) ||
    !journey.steps.length
  ) {
    return null;
  }

  const index = Number(journey.currentIndex || 0);
  const currentStep = journey.steps[index];
  if (!currentStep) return null;

  const isOnExpectedPage = location.pathname === currentStep.path;
  if (!isOnExpectedPage && location.pathname !== "/onboarding/marketplaces") {
    return <Navigate to={currentStep.path} replace />;
  }

  const goNext = () => {
    const nextIndex = index + 1;
    if (nextIndex >= journey.steps.length) {
      clearOnboardingJourney();
      navigate("/dashboard");
      return;
    }
    const updated = { ...journey, currentIndex: nextIndex };
    saveOnboardingJourney(updated);
    setJourney(updated);
    navigate(updated.steps[nextIndex].path);
  };

  const skipAll = () => {
    clearOnboardingJourney();
    navigate("/dashboard");
  };

  if (!isOnExpectedPage) return null;

  return (
    <div
      style={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 4000,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 12,
        minWidth: 280,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      }}
    >
      <p style={{ margin: 0, fontWeight: 600 }}>Setup journey</p>
      <p style={{ margin: "4px 0 10px", fontSize: 13, color: "#6b7280" }}>
        Step {index + 1}/{journey.steps.length}: {currentStep.label}
      </p>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          type="button"
          className="btn btn-light btn-sm"
          onClick={skipAll}
        >
          Skip for now
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={goNext}
        >
          Done, next
        </button>
      </div>
    </div>
  );
};

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AppStateProvider>
        <AuthProvider>
          <Router>
            <TrackPage />
            <OnboardingJourneyGuide />

            <div className="App">
              <Routes>
                {/* ============================================ */}
                {/* PUBLIC ROUTES (Login/Register Only) */}
                {/* ============================================ */}
                <Route path="/" element={<RootRedirect />} />
                <Route path="scout" element={<ChatbotPage />} />

                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />
                <Route path="/login-as" element={<LoginAsPage />} />
                <Route
                  path="/privacy-policy"
                  element={
                    <PublicRoute>
                      <PrivacyPolicyPage />
                    </PublicRoute>
                  }
                />

                <Route
                  path="/register"
                  element={
                    <PublicRoute>
                      <RegisterPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/onboarding/marketplaces"
                  element={
                    <ProtectedRoute>
                      <MarketplaceOnboardingPage />
                    </ProtectedRoute>
                  }
                />
                {/* Standalone print page (no layout/sidebar) */}
                <Route
                  path="/batches/print/:batchId"
                  element={
                    <ProtectedRoute>
                      <BatchPrintPage />
                    </ProtectedRoute>
                  }
                />

                {/* ============================================ */}
                {/* PROTECTED ROUTES (Dashboard & All Pages) */}
                {/* ============================================ */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  {/* Service */}
                  <Route path="services" element={<ServicePages />} />

                  <Route path="services" element={<ServicePages />} />
                  <Route
                    path="/demo"
                    element={
                      <PublicRoute>
                        <DemoPage />
                      </PublicRoute>
                    }
                  />
                  <Route path="chatbot" element={<ChatPage />} />
                  <Route path="kpi-dashboard" element={<KpiDashboard />} />

                  {/* Logger Tester - NAYA ROUTE */}
                  <Route path="logger-test" element={<LoggerTester />} />

                  {/* Dashboard Home */}
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="listing-agent" element={<ListingAgentPage />} />
                  {/* POS Page */}
                  <Route path="pos" element={<PosIndex />} />
                  <Route path="multimodel" element={<MultiModel />} />
                  {/* Products */}
                  <Route path="products" element={<ViewProductsPage />} />
                  <Route
                    path="/vinted-products"
                    element={<VintedProductsPage />}
                  />

                  <Route path="products/add" element={<AddProductPage />} />

                  {/* UPDATE: Update product route - product_id ke through */}
                  <Route
                    path="products/update/:id"
                    element={<UpdateProductPage />}
                  />

                  {/* NEW: View / Update Product */}
                  <Route path="products/:id" element={<ProductDetailsPage />} />

                  <Route
                    path="products/drafts"
                    element={<DraftProductsPage />}
                  />

                  {/* Integrations */}
                  <Route
                    path="integrations/shopify"
                    element={<ShopifyIntegrationPage />}
                  />
                  <Route
                    path="integrations/ebay/integrated"
                    element={<EbayIntegratedPage />}
                  />
                  <Route
                    path="integrations/ebay"
                    element={<EbayIntegrationPage />}
                  />
                  <Route
                    path="integrations/poshmark"
                    element={<PoshmarkIntegrationPage />}
                  />

                  {/* Settings */}
                  <Route path="settings/ebay" element={<EbaySettingsPage />} />
                  <Route
                    path="settings/shopify"
                    element={<ShopifySettingsPage />}
                  />
                  <Route
                    path="settings/general"
                    element={<GeneralSettingPage />}
                  />

                  {/* Invite Users */}
                  <Route path="invite-users" element={<InviteUsersPage />} />

                  {/* Sorter Tool */}
                  <Route path="sorter" element={<SorterPage />} />
                  {/* Chatbot Tool */}
                  {/* Batches (dummy UI) */}
                  <Route path="batches" element={<BatchesPage />} />
                  <Route
                    path="automation/ebay"
                    element={<EbayAutomationPage />}
                  />
                  <Route
                    path="notification/notify-vendors"
                    element={<NotifyVendors />}
                  />

                  <Route path="automation/fleek" element={<TestFleekPrice />} />
                  <Route
                    path="smartscanoverview"
                    element={<SmartScanOverview />}
                  />
                  {/* Order Workflow */}
                  <Route path="orders" element={<OrdersPage />} />
                  <Route
                    path="order-dashboard"
                    element={<OrderDashboardPage />}
                  />
                </Route>

                {/* ============================================ */}
                {/* 404 - CATCH ALL (Optional) */}
                {/* ============================================ */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </AppStateProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
